/**
 * Maps the current game host to the external lobby URL used on session end / sign-out.
 *
 * Add new environments here only — redirect logic should call resolveLobbyRedirectUrl().
 */

/** Fallback when hostname is unknown (AWS Production lobby). */
export const LOBBY_REDIRECT_DEFAULT_URL = 'https://portal.play-studios.ai';

/** Exact hostname → lobby URL */
export const LOBBY_REDIRECT_HOST_MAP: Readonly<Record<string, string>> = {
  // OVH Dev/QA
  'games.tzesst.com': 'https://games.tzesst.com',
  'cert.tzesst.com': 'https://cert.tzesst.com',
  // AWS Stage
  'lobby-stage.play-studios.ai': 'https://lobby-stage.play-studios.ai',
  'games-stage.play-studios.ai': 'https://lobby-stage.play-studios.ai',
  'client-stage.play-studios.ai': 'https://lobby-stage.play-studios.ai',

  // AWS Production
  'portal.play-studios.ai': 'https://portal.play-studios.ai',
  'games.play-studios.ai': 'https://portal.play-studios.ai',
  'client.play-studios.ai': 'https://portal.play-studios.ai',
};

/**
 * Ordered host rules (first match wins). Used when hostname is not in LOBBY_REDIRECT_HOST_MAP.
 */
export const LOBBY_REDIRECT_HOST_RULES: ReadonlyArray<{
  id: string;
  matches: (hostname: string) => boolean;
  lobbyUrl: string;
}> = [
  {
    id: 'ovh',
    matches: hostname =>
      hostname === 'games.tzesst.com' || hostname.endsWith('.tzesst.com'),
    lobbyUrl: 'https://games.tzesst.com',
  },
  {
    id: 'aws-stage',
    matches: hostname =>
      hostname.endsWith('.play-studios.ai') && hostname.includes('stage'),
    lobbyUrl: 'https://lobby-stage.play-studios.ai',
  },
  {
    id: 'aws-production',
    matches: hostname => hostname.endsWith('.play-studios.ai'),
    lobbyUrl: 'https://portal.play-studios.ai',
  },
];

function normalizeLobbyUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return LOBBY_REDIRECT_DEFAULT_URL;
  try {
    return new URL(trimmed).href;
  } catch {
    // Allow bare hosts / protocol-relative values from ?lobbyUrl=
    try {
      if (trimmed.startsWith('//')) {
        return new URL(`https:${trimmed}`).href;
      }
      if (!trimmed.startsWith('/') && !trimmed.startsWith('.')) {
        return new URL(`https://${trimmed}`).href;
      }
    } catch {
      /* fall through */
    }
    console.warn('[lobby-redirect] Invalid lobby URL in configuration:', url);
    return LOBBY_REDIRECT_DEFAULT_URL;
  }
}

/**
 * Resolves the lobby URL for the given host (defaults to window.location.hostname).
 * Optional runtime override (e.g. config.json) takes precedence when provided.
 */
export function resolveLobbyRedirectUrl(
  hostname: string = typeof window !== 'undefined'
    ? window.location.hostname
    : '',
  runtimeOverride?: string
): string {
  if (runtimeOverride) {
    return normalizeLobbyUrl(runtimeOverride);
  }

  const host = hostname.trim().toLowerCase();
  if (!host) {
    console.warn(
      '[lobby-redirect] Missing hostname; using default lobby URL:',
      LOBBY_REDIRECT_DEFAULT_URL
    );
    return LOBBY_REDIRECT_DEFAULT_URL;
  }

  const exact = LOBBY_REDIRECT_HOST_MAP[host];
  if (exact) {
    return normalizeLobbyUrl(exact);
  }

  for (const rule of LOBBY_REDIRECT_HOST_RULES) {
    if (rule.matches(host)) {
      return normalizeLobbyUrl(rule.lobbyUrl);
    }
  }

  console.warn(
    `[lobby-redirect] No lobby mapping for host "${host}"; using default:`,
    LOBBY_REDIRECT_DEFAULT_URL
  );
  return LOBBY_REDIRECT_DEFAULT_URL;
}
